import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  AnalyticsOverview,
  AnalyticsTopPost,
} from "../models/AnalyticsOverview";

interface PostPages {
  key: string;
  posts: AnalyticsTopPost[];
  nextOffset: number;
  rankingVersion?: string;
  total: number;
  hasMore: boolean;
}

/** Load bounded pages on demand; reset when filters or the leading ranking change. */
export function useAnalyticsPostPages(
  overview: AnalyticsOverview | null,
  connectionIds: string[],
  filterKey: string,
  disabled: boolean,
) {
  const { currentUser, activeWorkspaceId } = useAuth();
  const [pages, setPages] = useState<PostPages | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const connectionKey = [...connectionIds].sort().join(",");
  const key = JSON.stringify([
    activeWorkspaceId,
    filterKey,
    connectionKey,
    overview?.range.from,
    overview?.range.to,
    overview?.sort,
    overview?.topPosts.map((post) => post.publicationId),
    overview?.topPostsPagination?.total,
    overview?.topPostsPagination?.rankingVersion,
  ]);
  const activeKey = useRef(key);
  activeKey.current = key;

  useEffect(() => {
    request.current?.abort();
    request.current = null;
    setPages(null);
    setLoading(false);
    setError(null);
    return () => {
      request.current?.abort();
      request.current = null;
    };
  }, [key]);

  const current = pages?.key === key ? pages : null;
  const firstPage = overview?.topPosts ?? [];
  const refreshedFirstPage = new Map(firstPage.map(post => [post.publicationId, post]));
  const loadedPosts = current?.posts.map(post =>
    current.rankingVersion === overview?.topPostsPagination?.rankingVersion
      ? refreshedFirstPage.get(post.publicationId) ?? post
      : post
  ) ?? firstPage;
  const seen = new Set<string>();
  const posts = loadedPosts.filter((post) => {
    if (seen.has(post.publicationId)) return false;
    seen.add(post.publicationId);
    return true;
  });
  const hasMore =
    current?.hasMore ?? overview?.topPostsPagination?.hasMore ?? false;
  const total =
    current?.total ?? overview?.topPostsPagination?.total ?? posts.length;

  const loadMore = useCallback(async () => {
    if (
      disabled ||
      request.current ||
      !hasMore ||
      !overview ||
      !currentUser ||
      !activeWorkspaceId
    )
      return;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      if (controller.signal.aborted || activeKey.current !== key) return;
      const params = new URLSearchParams({
        from: String(overview.range.from),
        to: String(overview.range.to),
        sort: overview.sort,
        postOffset: String(current?.nextOffset ?? firstPage.length),
      });
      const rankingVersion = current?.rankingVersion ?? overview.topPostsPagination?.rankingVersion;
      if (rankingVersion) params.set("postRankingVersion", rankingVersion);
      if (connectionKey) params.set("connectionIds", connectionKey);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/analytics/overview?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-workspace-id": activeWorkspaceId,
          },
          signal: controller.signal,
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.error || "More posts couldn’t be loaded. Please try again.",
        );
      if (controller.signal.aborted || activeKey.current !== key) return;
      const page = result as AnalyticsOverview;
      const pagination = page.topPostsPagination;
      if (!pagination)
        throw new Error("More posts couldn’t be loaded. Please try again.");
      setPages({
        key,
        // A changed ranking returns offset zero. Replace every old page so an
        // unseen post that moved upward cannot be lost behind the old offset.
        posts: pagination.offset === 0
          ? page.topPosts
          : [...(current?.posts ?? firstPage), ...page.topPosts],
        rankingVersion: pagination.rankingVersion,
        nextOffset: pagination.offset + page.topPosts.length,
        total: pagination.total,
        hasMore: pagination.hasMore,
      });
    } catch (failure) {
      if (!controller.signal.aborted && activeKey.current === key) {
        setError(
          failure instanceof Error
            ? failure.message
            : "More posts couldn’t be loaded. Please try again.",
        );
      }
    } finally {
      if (request.current === controller) {
        request.current = null;
        setLoading(false);
      }
    }
  }, [
    disabled,
    hasMore,
    overview,
    currentUser,
    activeWorkspaceId,
    key,
    current,
    firstPage.length,
    connectionKey,
  ]);

  return { posts, total, hasMore, loading, error, loadMore };
}
